import React from 'react';
import PropTypes from 'prop-types';

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import classnames from 'classnames';
import i18n from 'i18next';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';

import { Button } from 'uikit/buttons';

import Filter from './filter';
import FilterToggleButton from './filterToggleButton';

import styles from '../styles/filters.module.scss';

const Filters = ({
  className = '',
  clearButtonIcon = <RemoveCircleOutlineOutlinedIcon />,
  filters = [],
  hasClearButton = true,
  hasCustomFilterButton = false,
  isCustomFilterButtonDisabled = false,
  isDisabled = false,
  onClearButtonClick = () => {},
  onCustomFilterClick = () => {},
  onSelectionUpdate = () => {},
  position = 'right',
  selection = {},
}) => {
  if (!filters.length) return null;

  const convertToArray = (value) => [].concat(value ?? []);
  const isFiltering = Object.values(selection).some((value) => convertToArray(value).length);

  return (
    <div
      className={classnames(
        styles.element,
        className,
        position === 'left' && styles.isPositionLeft,
      )}
    >
      {hasClearButton && isFiltering && (
        <Button
          className={styles.clearButton}
          icon={clearButtonIcon}
          isDisabled={isDisabled}
          onClick={onClearButtonClick}
          size="small"
          text={i18n.t('clear-filters')}
          variant="ghost"
        />
      )}
      <div className={styles.filters}>
        {hasCustomFilterButton && (
          <Button
            color="brand"
            icon={<AutoAwesomeOutlinedIcon />}
            isDisabled={isDisabled || isCustomFilterButtonDisabled}
            onClick={onCustomFilterClick}
            size="small"
            text={i18n.t('custom-filter')}
            variant="outlined"
          />
        )}
        {filters.map(({ isToggle, key, extraParams, ...filter }) => {
          const Element = isToggle ? FilterToggleButton : Filter;

          return (
            <Element
              dropdownPosition={position}
              key={key}
              isAsync={filter.is_async}
              isDisabled={isDisabled}
              isSingleSelect={!filter.allow_multiple_selects}
              name={filter.label}
              onSelectionUpdate={onSelectionUpdate}
              options={filter.options}
              param={key}
              selection={isToggle ? selection[key] : convertToArray(selection[key])}
              extraParams={extraParams}
            />
          );
        })}
      </div>
    </div>
  );
};

Filters.propTypes = {
  className: PropTypes.string,
  clearButtonIcon: PropTypes.element,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      allow_multiple_selects: PropTypes.bool,
      is_async: PropTypes.bool,
      isToggle: PropTypes.bool,
      key: PropTypes.string,
      label: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
        }),
      ),
      extraParams: PropTypes.shape({}),
    }),
  ),
  hasClearButton: PropTypes.bool,
  hasCustomFilterButton: PropTypes.bool,
  isCustomFilterButtonDisabled: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onClearButtonClick: PropTypes.func,
  onCustomFilterClick: PropTypes.func,
  onSelectionUpdate: PropTypes.func,
  position: PropTypes.oneOf(['left', 'right']),
  selection: PropTypes.shape({}),
};

export default React.memo(Filters);
